import axios from '@/plugins/axios';
import {
  ChatGroup, ChatGroupMember, ChatGroupMeta, ChatGroupMetaEntry, ChatMessage, ChatMessageType
} from '@/models/Messenger';
import BCXHTMLSanitizer from '@/utils/BCXHTMLSanitizer';

interface ExternalGroupMessage {
  messageId: string;
  creator: string; // ExternalId serialized as a bare userId string
  text: string;
  created: string;
  edited: boolean;
  read?: boolean;
}
interface GroupMessagesResponse {
  messages: ExternalGroupMessage[];
  hasMore: boolean;
}

export interface GroupMessagesPage {
  messages: ChatMessage[];
  hasMore: boolean;
}

const toChatMessage = (m: ExternalGroupMessage): ChatMessage => ({
  messageId: m.messageId,
  text: m.text,
  created: m.created,
  creator: m.creator,
  read: m.read ?? true,
  edited: m.edited,
  flagged: false,
  subMessages: [],
  upvoteCount: 0,
  downvoteCount: 0,
  voteResult: 0,
  messageType: ChatMessageType.PRIVATE_MESSAGING_MESSAGE
});

const MessengerGroupService = {
  async getGroups(): Promise<ChatGroup[]> {
    const response = await axios.get<ChatGroup[]>('/v1/messaging/groups');
    return response.data ?? [];
  },

  async getGroupsMeta(): Promise<ChatGroupMetaEntry[]> {
    const response = await axios.get<ChatGroupMeta>('/v1/messaging/groups/meta');
    return response.data?.metaList ?? [];
  },

  async getMembers(groupId: string): Promise<ChatGroupMember[]> {
    const response = await axios.get<ChatGroupMember[]>(`/v1/messaging/groups/${groupId}/members`);
    return response.data ?? [];
  },

  async getGroupMessages(
    groupId: string,
    { beforeMessageId, limit }: { beforeMessageId?: string; limit: number }
  ): Promise<GroupMessagesPage> {
    const response = await axios.get<GroupMessagesResponse>(`/v1/messaging/groups/${groupId}/messages`, {
      params: { beforeMessageId, limit }
    });
    const messages = (response.data?.messages ?? []).map(toChatMessage).reverse();
    return { messages, hasMore: response.data?.hasMore ?? false };
  },

  async postGroupMessage(groupId: string, messageContent: string): Promise<ChatMessage> {
    const content = BCXHTMLSanitizer.beforeSave(messageContent);
    const response = await axios.post<ExternalGroupMessage>(`/v1/messaging/groups/${groupId}/messages`, { messageContent: content });
    return toChatMessage(response.data);
  },

  async editGroupMessage(groupId: string, messageId: string, messageContent: string): Promise<ChatMessage> {
    const content = BCXHTMLSanitizer.beforeSave(messageContent);
    const response = await axios.put<ExternalGroupMessage>(`/v1/messaging/groups/${groupId}/messages/${messageId}`, { messageContent: content });
    return toChatMessage(response.data);
  },

  async markGroupRead(groupId: string): Promise<void> {
    await axios.post(`/v1/messaging/groups/${groupId}/messages/read`);
  },

  async createGroup(title: string): Promise<ChatGroup> {
    const response = await axios.post<ChatGroup>('/v1/messaging/groups', { title });
    return response.data;
  },

  async renameGroup(id: string, title: string): Promise<ChatGroup> {
    const response = await axios.put<ChatGroup>(`/v1/messaging/groups/${id}`, { title });
    return response.data;
  },

  async deleteGroup(id: string): Promise<void> {
    await axios.delete(`/v1/messaging/groups/${id}`);
  },

  async addMember(id: string, userId: string, personalMessage?: string): Promise<void> {
    const message = personalMessage?.trim();
    await axios.post(`/v1/messaging/groups/${id}/invitations`, {
      invitee: userId,
      ...(message ? { personalMessage: message } : {})
    });
  },

  async removeMember(id: string, userId: string): Promise<void> {
    await axios.delete(`/v1/messaging/groups/${id}/members/${userId}`);
  },

  async leaveGroup(id: string): Promise<void> {
    await axios.delete(`/v1/messaging/groups/${id}/membership`);
  },

  async joinGroup(id: string): Promise<ChatGroup> {
    const response = await axios.post<ChatGroup>(`/v1/messaging/groups/${id}/membership`);
    return response.data;
  },
};

export default MessengerGroupService;
